import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Flame, ChevronRight } from "lucide-react";

const ST = { expired: ["Expired", "bg-rose-50 text-rose-700 border-rose-200"], due_soon: ["Due soon", "bg-amber-50 text-amber-800 border-amber-300"], in_progress: ["In progress", "bg-amber-50 text-amber-800 border-amber-300"], not_started: ["Not started", "bg-slate-100 text-slate-600 border-slate-200"] };

function Ring({ pct }) {
  const color = pct >= 90 ? "#15803d" : pct >= 60 ? "#b45309" : "#b91c1c";
  return (
    <div className="relative h-16 w-16 shrink-0" data-testid="hazmat-widget-ring">
      <div className="h-16 w-16 rounded-full" style={{ background: `conic-gradient(${color} ${pct * 3.6}deg, #e2e8f0 0)` }} />
      <div className="absolute inset-1.5 rounded-full bg-white flex items-center justify-center font-mono font-bold text-sm" style={{ color }}>{pct}%</div>
    </div>
  );
}

export function HazmatWidget({ customerId, isAdmin, linkTo }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/company/hazmat/overview", { params: { customer_id: customerId || undefined } }).then(({ data }) => setRows(data)).catch(() => setRows([])); }, [customerId]);
  if (!rows || rows.length === 0) return null;
  return (
    <Card className="overflow-hidden border-t-4 border-t-orange-500 mb-6" data-testid="hazmat-widget">
      <div className="px-5 py-3 border-b border-orange-100 bg-orange-50/60 flex items-center justify-between">
        <div className="flex items-center gap-2 font-semibold text-slate-900"><Flame className="h-4 w-4 text-orange-700" />Hazmat audit readiness<span className="text-xs font-mono text-slate-500">({rows.length} {rows.length === 1 ? "carrier" : "carriers"})</span></div>
        <span className="text-xs text-orange-800">Expired · due ≤60 days · not yet verified</span>
      </div>
      <div className="divide-y divide-slate-100">
        {rows.map((r) => (
          <div key={r.customer_id} className="p-4 flex gap-4 items-start" data-testid={`hazmat-widget-row-${r.customer_id}`}>
            <Ring pct={r.readiness} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="font-semibold text-slate-900">{isAdmin && !customerId ? r.company : "Requirements"}<span className="text-xs text-slate-500 font-normal ml-2">{r.compliant}/{r.applicable} compliant</span>
                  {r.expired > 0 && <Badge className="ml-2 bg-rose-50 text-rose-700 border border-rose-200">{r.expired} expired</Badge>}{r.due_soon > 0 && <Badge className="ml-1 bg-amber-50 text-amber-800 border border-amber-300">{r.due_soon} due soon</Badge>}</div>
                <Link to={linkTo ? linkTo(r) : (isAdmin ? `/customers/${r.customer_id}` : "/company")} className="text-xs text-orange-700 hover:underline inline-flex items-center" data-testid={`hazmat-widget-link-${r.customer_id}`}>Open hazmat tab<ChevronRight className="h-3 w-3" /></Link>
              </div>
              {r.attention.length === 0 ? <div className="text-sm text-emerald-700 mt-1">All applicable requirements verified. Audit-ready.</div> : (
                <ul className="mt-2 grid md:grid-cols-2 gap-x-6 gap-y-1">
                  {r.attention.map((a) => { const [l, cls] = ST[a.status] || ST.not_started; return (
                    <li key={a.key} className="flex items-center justify-between gap-2 text-sm py-0.5"><span className="truncate text-slate-800">{a.label}</span><Badge className={`border shrink-0 ${cls}`}>{l}{a.days != null && a.status !== "not_started" && a.status !== "in_progress" ? (a.days < 0 ? ` · ${-a.days}d ago` : ` · ${a.days}d`) : ""}</Badge></li>
                  ); })}
                  {r.attention_total > r.attention.length && <li className="text-xs text-slate-500">+{r.attention_total - r.attention.length} more</li>}
                </ul>
              )}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
